import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { observer } from 'mobx-react';
import { BottomBar, BottomBarActions } from '@monorepo/base/src/components/bottom-bar/bottom-bar';
import { PrimaryButton } from '@monorepo/base/src/components/buttons/buttons';
import { Disable } from '@monorepo/base/src/components/disable/disable';
import { Page, Pagebar, PagebarTitle } from '@monorepo/base/src/components/page/page';
import { DataAttribute } from '@monorepo/tools/src/lib/models/data-attr.model';
import styles from './create-bidder.module.scss';
import { ErrorMsg } from '../../../modules/components/error-msg/error-msg';
import { BidderAdvertiserForm } from '../../../modules/components/forms/bidder-advertiser/bidder-advertiser.form';
import { DispatchLogsTypes, useLogs } from '@monorepo/controlled/src/hooks/use-logs';
import { useNavigateParams } from '@monorepo/tools/src/lib/hooks/tools/use-navigate-params';
import { Fragment, useEffect, useState } from 'react';
import routes from '../../../modules/routes/index.routes';
import { BidderApi } from '../../../modules/apis/bidder.api';
import { ActionModal, ModalTypes } from '@monorepo/controlled/src/components/modal/modals/action-modal/action-modal';
import { useBidderAdvertiserTableOfContents } from '../../../modules/hooks/toc/bidder-adveritser.toc';
import { Nav, NavGroup, NavItem, NavPage } from '@monorepo/base/src/components/nav/nav';
import { TableOfContents } from '@monorepo/base/src/components/table-of-contents/table-of-contents';
import { useRoute } from '@monorepo/tools/src/lib/hooks/tools/use-route';
import { IAdminxStore } from '../../../modules/stores';

export const CreateBidderAdvertiser = observer(() => {
	const { bidderAdvertiserStore, modalsStore } = useStores<IAdminxStore>();
	const [isSaving, setIsSaving] = useState(false);
	const bidderAdvertiserCrudStore = bidderAdvertiserStore.getCrud();
	const isLoading = bidderAdvertiserCrudStore.getIsLoading();
	const isSuccess = bidderAdvertiserCrudStore.getIsSuccess();
	const error = bidderAdvertiserCrudStore.getHttpError();
	const advertiser = bidderAdvertiserCrudStore.getData().advertiser;
	const navigate = useNavigateParams();
	const { dispatchLog } = useLogs();
	const { tableOfContents, isTableOfContents, setIsTableOfContents } = useBidderAdvertiserTableOfContents();
	const { currentRoute } = useRoute();
	const [activeRoute, setActiveRoute] = useState<string>(currentRoute);

	useEffect(() => {
		setIsTableOfContents(true);

		return () => {
			bidderAdvertiserCrudStore.reset();
		};
	}, []);

	useEffect(() => {
		if (isSuccess) {
			navigate(routes.bidderAdvertisers.advertisers());
			dispatchLog({
				msg: `Advertiser ${advertiser?.advertiser_name} was successfully edited`,
				type: DispatchLogsTypes.Success,
			});
			bidderAdvertiserCrudStore.setIsSuccess(false);
		}
	}, [isSuccess]);

	const onCreateAdvertiser = () => {
		if (!bidderAdvertiserCrudStore.isValid()) {
			dispatchLog({
				msg: 'Form is invalid',
				type: DispatchLogsTypes.Error,
			});
			return;
		}
		setIsSaving(true);
		const formData = bidderAdvertiserCrudStore.getCreateFormData().toObject();
		const advertiserPayload = {
			name: formData.name,
			priority: formData.priority,
			total_budget: formData.total_budget,
			should_receive_l1: formData.should_receive_l1,
			use_enhanced_black_list: formData.use_enhanced_black_list,
		};
		BidderApi.createAdvertiser(advertiserPayload)
			.then(() => {
				navigate(routes.bidderAdvertisers.advertisers());
				dispatchLog({
					msg: `Advertiser ${advertiser?.advertiser_name} was successfully created`,
					type: DispatchLogsTypes.Success,
				});
			})
			.catch((e: { data: { success: boolean; errors: { message: string; field: string }[] } }) => {
				modalsStore.addModal({
					children: (
						<ActionModal type={ModalTypes.Error} primaryBtnTitle={'OK'}>
							<div className={styles.errorWrapper}>
								<h2 className={styles.title}>{`Failed to create advertiser.`}</h2>
								{e.data.errors.map(error => (
									<div className={styles.errorContent}>{`${error.field}: ${error.message}`}</div>
								))}
							</div>
						</ActionModal>
					),
				});
			})
			.finally(() => {
				setIsSaving(false);
			});
	};

	return (
		<NavPage>
			<Nav onNavClick={({ to }) => to && setActiveRoute(to)} activeRoute={activeRoute}>
				<NavItem to={`${routes.bidderAdvertisers.advertisers()}`}>Advertisers</NavItem>
				<Fragment>
					{isTableOfContents && (
						<NavGroup title={'Table of contents'}>
							<TableOfContents heads={tableOfContents} />
						</NavGroup>
					)}
				</Fragment>
			</Nav>
			<Page unstyled={true}>
				<Disable is={isLoading || isSaving}>
					<Page>
						<Pagebar>
							<PagebarTitle unstyled={true}>
								<div className={styles.title}>Create an advertiser</div>
							</PagebarTitle>
						</Pagebar>
						<BidderAdvertiserForm />
					</Page>
					<BottomBar>
						{error ? <ErrorMsg error={error} /> : null}
						<BottomBarActions>
							<PrimaryButton
								onClick={() => onCreateAdvertiser()}
								isRotateIcon={isLoading}
								icon={isLoading ? 'loading-01' : 'send-03'}
								debugProps={{ dataAttrs: [new DataAttribute('id', 'create_campaign_button')] }}>
								Create
							</PrimaryButton>
						</BottomBarActions>
					</BottomBar>
				</Disable>
			</Page>
		</NavPage>
	);
});
